import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import type { LoginMethod } from '@linktivity/ars-neo';
import { BaseAuth } from '@link/share/stores';
import {
  buildLocalizedPath,
  buildPathWithRedirect,
  readRedirectParam,
  type ProfileGuardOptions
} from '@link/share/utils';
import { resolveGa4LoginEntry } from '@link/share/vendors';
import ShareAuth from '@link/share/vendors/firebase';
import Login from './Login';
import styles from './loginContainer.module.css';

interface LoginContainerProps {
  auth: BaseAuth;
  firebase: ShareAuth;
  loginMethods?: LoginMethod[];
  profileGuard?: ProfileGuardOptions;
}

const LoginContainer = observer(
  ({ auth, firebase, loginMethods, profileGuard }: LoginContainerProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const skipOtpCheck = location.state?.skipOtpCheck as boolean | undefined;
    const ga4Entry = resolveGa4LoginEntry({
      state: location.state,
      locationKey: location.key,
      hasRedirect: !!readRedirectParam(new URLSearchParams(location.search))
    });

    const handleAuthSuccess = useCallback(
      (redirectTo: string, isNewUser: boolean) => {
        navigate(
          buildPathWithRedirect(
            buildLocalizedPath('email-verification'),
            redirectTo
          ),
          {
            state: { isNewUser, skipOtpCheck, autoResendOtp: true },
            replace: true
          }
        );
      },
      [navigate, skipOtpCheck]
    );

    return (
      <div className={styles.login}>
        <Login
          auth={auth}
          loggedIn={auth.loggedIn}
          firebase={firebase}
          loginMethods={loginMethods}
          profileGuard={profileGuard}
          onAuthSuccess={handleAuthSuccess}
          requireVerified={!!skipOtpCheck}
          ga4Entry={ga4Entry}
        />
      </div>
    );
  }
);

export default LoginContainer;
